<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { SelectedPCPOption } from '../Product/ProfessionalCarePlan.vue'

type AddedProduct = {
  productId: string
  product: any
  selectedPcpOption: SelectedPCPOption | null
  showPcp: boolean
  /** Its promotions that no product above it carries, so a shared gift is offered once. */
  bonusPromotions: ShopperProductsTypes.ProductPromotion[]
}

const { bagUrl } = useSiteUrl()
const basketsStore = useBasketsStore()
const preferencesStore = usePreferencesStore()
const { pcpEnabled } = useFeatureFlags()
const { locale } = useI18n()

provide('location', LOCATIONS.addToBagPopup)

const addedProducts = ref<AddedProduct[]>([])
const latestProduct = computed(() => addedProducts.value.at(-1))
const originProduct = computed(() => addedProducts.value[0])
const seedItemId = computed(() => mapConstructorItemId(originProduct.value?.product))
const addedCartItems = computed(() =>
  addedProducts.value.map((added) => {
    const item = basketsStore.getItem(added.productId)
    return item && { ...item, productId: item.productId || '', productName: item.productName || '' }
  }),
)
const recommendationsSession = ref(0)
/** Each gift sits under the product that was newest when it was claimed, so later adds stack below it. */
const giftsWithPurchase = ref<{ product: any; afterIndex: number }[]>([])
const hasPCPUpdated = ref(false)

const productsRef = ref<HTMLElement | null>(null)

basketsStore.$onAction(({ name, args, after }) => {
  if (name === 'addItem') {
    const [{ productId, popup }] = args
    if (!popup || popup.isGiftWithPurchase) return

    // The open slide-in covers the page, so an add made while it's open came from its own upsell.
    const isAppending = basketsStore.isShowAddToBagPopup
    after(() => {
      if (basketsStore.error) return
      const { selectedPcpOption, product } = popup
      const offered = new Set(
        isAppending ? addedProducts.value.flatMap((added) => added.bonusPromotions.map((p) => p.promotionId)) : [],
      )
      const added: AddedProduct = {
        productId: productId || product?.id,
        product,
        selectedPcpOption,
        showPcp:
          pcpEnabled() && product?.c_pcpEligibleProduct && (!selectedPcpOption || selectedPcpOption?.id === 'PCPNO'),
        bonusPromotions: (product?.productPromotions ?? []).filter(
          (promotion: ShopperProductsTypes.ProductPromotion) => !offered.has(promotion.promotionId),
        ),
      }
      hasPCPUpdated.value = false

      if (isAppending) {
        addedProducts.value.push(added)
        scrollToLatestProduct()
        return
      }
      addedProducts.value = [added]
      giftsWithPurchase.value = []
      recommendationsSession.value++
    })
  }

  if (name === 'updateItem') {
    after((result) => {
      // @TODO: Confirm success & handle error
      hasPCPUpdated.value = true
    })
  }
})

const selectedPCPInPopup = ref()

function onPcpSelected(option: SelectedPCPOption) {
  const cartItem = latestProduct.value && basketsStore.getItem(latestProduct.value.productId)
  if (!cartItem?.itemId) return
  selectedPCPInPopup.value = option
  basketsStore.updateItem({
    itemId: cartItem.itemId,
    optionItems: [{ optionId: 'pcp', optionValueId: option.id }],
  })
}

async function scrollToLatestProduct() {
  await nextTick()
  // Claimed gifts share the container, so only the products are counted.
  const products = productsRef.value?.querySelectorAll(':scope > .add-to-bag-popup__product')
  products?.[products.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function handleClose() {
  hasPCPUpdated.value = false
  giftsWithPurchase.value = []
  basketsStore.isShowAddToBagPopup = false
}
</script>

<template>
  <Modal
    class="add-to-bag-popup"
    data-testid="add-to-bag-popup"
    :data-test-recommender="preferencesStore.site?.addToBagRecommender?.fields?.recommenderID"
    :show="basketsStore.isShowAddToBagPopup"
    border-on-scroll
    @close="handleClose"
  >
    <template #header>
      <h2 class="add-to-bag-popup__title" data-testid="add-to-bag-popup__title">
        {{ $t('product_details_page.add_to_bag_popup.success_message') }}
      </h2>
    </template>

    <div class="add-to-bag-popup__wrap">
      <AlertBar variant="success" v-if="hasPCPUpdated">
        {{
          selectedPCPInPopup?.id === 'PCPNO'
            ? $t('product_details_page.add_to_bag_popup.pcp_callout_removed')
            : $t('product_details_page.add_to_bag_popup.pcp_callout_added', { name: selectedPCPInPopup?.name })
        }}
      </AlertBar>

      <div ref="productsRef" class="add-to-bag-popup__products">
        <template v-for="(added, index) in addedProducts" :key="`${added.productId}-${index}`">
          <div
            class="add-to-bag-popup__product"
            data-testid="add-to-bag-popup__product"
            :data-test-item-id="mapConstructorItemId(added.product)"
          >
            <ProductCardSummary v-if="addedCartItems[index]" :product="addedCartItems[index]" />
            <div
              v-if="added === latestProduct && added.showPcp"
              class="add-to-bag-popup__pcp-contents"
              data-testid="add-to-bag-popup__pcp"
            >
              <ProfessionalCarePlan :product="added.product" @pcp-selected="onPcpSelected" />
            </div>
          </div>
          <Transition
            v-for="gift in giftsWithPurchase.filter((gift) => gift.afterIndex === index)"
            :key="gift.product.productId"
            name="slide-fade"
            appear
          >
            <ProductCardSummary :product="gift.product" data-testid="add-to-bag-popup__gift" />
          </Transition>
        </template>
      </div>

      <div class="add-to-bag-popup__promo">
        <div class="add-to-bag-popup__promo-top">
          <client-only>
            <RichText :document="preferencesStore.pdp?.businessUSPMessage" class="add-to-bag-popup__business-usp" />
          </client-only>
          <div
            v-if="latestProduct?.selectedPcpOption && latestProduct.selectedPcpOption.id !== 'PCPNO'"
            class="add-to-bag-popup__pcp__callout"
          >
            <svg-icon name="mhj:success" />
            <span>
              {{ $t('product_details_page.professional_care_plan.title') }}:
              {{ latestProduct.selectedPcpOption.name }}
            </span>
          </div>
          <!-- 
            NOTE: Business has agreed to hard-coding the free shipping threshold of $100, but this may change in the future.
            As of 19/02/26 trade confirmed shipping for NZ will be free for all orders confirmed happy for this to be hard coded aswell 
          -->
          <div
            v-if="((basketsStore.basket && basketsStore.basket.productTotal) || 0) >= 100 && locale !== 'en-NZ'"
            class="add-to-bag-popup__free-shipping"
          >
            <svg-icon name="mhj:success" />
            <span>{{ $t('product_details_page.add_to_bag_popup.free_shipping') }}</span>
          </div>
        </div>

        <div class="add-to-bag-popup__upsell">
          <!-- One per product, so stacking another never refetches or redraws the offers already shown. -->
          <BonusProductPromotion
            v-for="(added, index) in addedProducts"
            :key="`${added.productId}-${index}`"
            :promotions="added.bonusPromotions"
            @promo-added="(product) => giftsWithPurchase.push({ product, afterIndex: addedProducts.length - 1 })"
          />
          <AddToBagRecommendations v-if="originProduct" :key="recommendationsSession" :seed-item-id="seedItemId" />
        </div>
      </div>
    </div>

    <div class="add-to-bag-sticky__buttons">
      <Anchor appearance="Button" variant="primary" :url="bagUrl" data-testid="add-to-bag-popup-view-bag">
        {{ $t('product_details_page.add_to_bag_popup.button.link') }}
      </Anchor>
      <Button
        appearance="Link"
        variant="tertiary"
        data-testid="add-to-bag-popup__continue"
        @click="basketsStore.isShowAddToBagPopup = false"
      >
        {{ $t('product_details_page.add_to_bag_popup.button.text') }}
      </Button>
    </div>
  </Modal>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.modal.add-to-bag-popup {
  --menu-header-height: 4.5rem;

  &.modal-popup--small {
    width: min-content;
  }

  ::backdrop {
    background: transparent; /* Match to PROD */
  }

  .modal__body {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .modal__header {
    padding: 0 var(--space-120) 0 var(--space-60);
  }

  .add-to-bag-popup__wrap {
    align-items: center;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: var(--space-60);
    padding: var(--space-60);
    padding-bottom: 12rem;
    max-width: 32.5rem;
  }

  .alert-bar {
    width: 100%;
  }

  .add-to-bag-popup__title {
    margin: 0;
    text-transform: uppercase;
    font-size: var(--text-xxlarge);
  }

  .add-to-bag-popup__products {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    width: 100%;
  }

  .add-to-bag-popup__product {
    scroll-margin-top: calc(var(--menu-header-height) + var(--space-50));
  }

  .add-to-bag-popup__products .product-card--summary {
    padding: var(--space-50);
    border: 1px solid var(--border-neutral-20);
  }

  .add-to-bag-popup__pcp-contents {
    text-align: left;
    .pcp {
      border: none;
      background-color: var(--surface-light-20);
    }
  }

  .add-to-bag-sticky__buttons {
    background-color: var(--ui-neutral-00);
    bottom: 0;
    border-top: 1px solid var(--border-neutral-20);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-60);
    max-width: 25.875rem;
    padding: var(--space-70) var(--space-60) var(--space-80) var(--space-60);
    position: fixed;
    width: 100%;
    z-index: calc(var(--z-modal) + 1);

    a {
      width: 100%;
    }
    button {
      width: min-content;
      white-space: nowrap;
    }
    button.link-tertiary {
      text-transform: uppercase;
    }
  }

  .add-to-bag-popup__promo {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
    align-self: flex-start;
    gap: var(--space-50);
    width: 100%;

    .add-to-bag-popup__promo-top {
      align-items: flex-start;
      align-self: flex-start;
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .add-to-bag-popup__pcp__callout {
      display: flex;
      align-items: center;
      gap: var(--space-30);
      font-size: var(--text-base);
      margin-top: var(--space-30);

      .icon {
        width: 1.5rem;
        height: 1.5rem;
      }
    }

    .add-to-bag-popup__business-usp {
      margin-bottom: 0;
      p {
        display: flex;
        align-items: center;
        text-transform: capitalize;
        font-size: var(--text-base);
        margin-bottom: 0;

        .icon {
          display: flex;
          align-items: center;
          margin-right: var(--space-30);
        }

        .icon__img {
          width: 1.5rem;
          height: 1.5rem;
        }
      }
    }

    .add-to-bag-popup__free-shipping {
      align-items: center;
      display: flex;
      gap: var(--space-30);
      font-size: var(--text-base);
      margin-top: var(--space-30);

      .icon {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }

  .add-to-bag-popup__upsell {
    display: flex;
    flex-direction: column;
    gap: var(--space-80);
    margin-top: var(--space-60);

    /* A gift offer lands after its lookup, so it grows into place and the carousel eases down rather than jumping. */
    .fade-out-enter-active {
      overflow: hidden;
      transition:
        max-height 0.4s ease-out,
        margin-bottom 0.4s ease-out,
        opacity 0.3s ease-out 0.15s;
    }

    .fade-out-enter-from {
      max-height: 0;
      margin-bottom: calc(-1 * var(--space-80));
      opacity: 0;
    }

    .fade-out-enter-to {
      max-height: 16rem;
    }
  }

  .slide-fade-enter-active {
    transition: all 0.4s ease-out;
  }

  .slide-fade-enter-from {
    transform: translateY(-20px);
    opacity: 0;
  }

  .slide-fade-enter-to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
